import { create } from 'zustand'
import { persist, devtools, createJSONStorage } from 'zustand/middleware'
import {
  Message,
  Conversation,
  MessageSender,
  PendingMessage,
} from '../routes/plus/models/Conversation.model'

// Constants
const MAX_PINNED_CONVERSATIONS = 3

// Types for rollback functionality
type RollbackSnapshot = {
  isPinned: boolean
  originalCreatedAt?: number
}

type ConversationsState = {
  conversations: Conversation[]
  conversationsNextToken: string | null
  pendingFirstMessage: PendingMessage | null
  isSidebarCollapsed: boolean
  lastApplicationId: string | null
  searchTerm: string | null
}

type ConversationsActions = {
  createConversation: (conversationId: string, title: string) => string
  setConversations: (
    conversations: Conversation[],
    applicationId: string | null,
    conversationsNextToken?: string | null
  ) => void
  appendConversations: (
    newConversations: Conversation[],
    conversationsNextToken?: string | null
  ) => void
  getConversation: (conversationId: string | null) => Conversation | null
  removeConversation: (conversationId: string) => void
  updateConversationName: (conversationId: string, newName: string) => void
  addMessage: (conversationId: string, msg: Message) => void
  setMessages: (
    conversationId: string,
    messages: Message[],
    messagesNextToken?: string | null
  ) => void
  prependMessages: (
    conversationId: string,
    olderMessages: Message[],
    newMessagesNextToken?: string | null
  ) => void
  setPendingFirstMessage: (msg: PendingMessage | null) => void
  setAwaitingResponse: (conversationId: string, isAwaiting: boolean) => void
  toggleSidebar: () => void
  setSearchTerm: (term: string | null) => void
  pinConversation: (id: string) => { success: boolean; error?: string }
  unpinConversation: (id: string) => void
  rollbackPinState: (id: string, prevState: RollbackSnapshot) => void
  updateConversationPinMetadata: (id: string, isPinned: boolean, pinTime?: number) => void
}

type ConversationsGetters = {
  getLastBotMessage: (conversationId: string | null) => Message | null
  selectPinnedConversations: () => Conversation[]
  selectRegularConversations: () => Conversation[]
  getPinnedConversationsCount: () => number
}

type ConversationsStore = ConversationsState & ConversationsActions & ConversationsGetters

const useConversationsStore = create<ConversationsStore>()(
  devtools(
    persist(
      (set, get) => ({
        conversations: [],
        conversationsNextToken: null,
        lastApplicationId: null,

        pendingFirstMessage: null,

        createConversation: (conversationId, title) => {
          const newConversation: Conversation = {
            id: conversationId,
            createdAt: Date.now(),
            title: title || `${conversationId.slice(0, 8)}`,
            messages: [],
            messagesNextToken: null,
            isAwaitingResponse: false,
            isPinned: false,
          }
          set((state) => ({
            conversations: [...state.conversations, newConversation],
          }))
          return conversationId
        },

        setConversations: (newConversations, applicationId, conversationsNextToken = null) => {
          set({
            conversations: newConversations,
            conversationsNextToken: conversationsNextToken,
            lastApplicationId: applicationId,
          })
        },

        appendConversations: (newConversations, conversationsNextToken = undefined) => {
          set((state) => ({
            conversations: [...state.conversations, ...newConversations],
            conversationsNextToken:
              conversationsNextToken !== undefined ? conversationsNextToken : null,
          }))
        },

        getConversation: (conversationId) => {
          if (!conversationId) return null
          return get().conversations.find((c) => c.id === conversationId) || null
        },

        removeConversation: (conversationId) => {
          set((state) => ({
            conversations: state.conversations.filter((c) => c.id !== conversationId),
          }))
        },

        updateConversationName: (conversationId, newName) => {
          set((state) => ({
            conversations: state.conversations.map((convo) =>
              convo.id === conversationId ? { ...convo, title: newName } : convo
            ),
          }))
        },

        addMessage: (conversationId, msg) => {
          set((state) => ({
            conversations: state.conversations.map((c) =>
              c.id === conversationId
                ? { ...c, messages: [...c.messages, msg], isAwaitingResponse: false }
                : c
            ),
          }))
        },

        setMessages: (conversationId, messages, messagesNextToken) => {
          set((state) => ({
            conversations: state.conversations.map((c) =>
              c.id === conversationId
                ? {
                    ...c,
                    messages: [...messages].sort((a, b) => a.createdAt - b.createdAt),
                    messagesNextToken:
                      messagesNextToken !== undefined ? messagesNextToken : c.messagesNextToken,
                  }
                : c
            ),
          }))
        },

        prependMessages: (conversationId, olderMessages, newMessagesNextToken) => {
          set((state) => ({
            conversations: state.conversations.map((c) => {
              if (c.id === conversationId) {
                const sortedOlderMessages = [...olderMessages].sort(
                  (a, b) => a.createdAt - b.createdAt
                )

                return {
                  ...c,
                  messages: [...sortedOlderMessages, ...c.messages],
                  messagesNextToken:
                    newMessagesNextToken !== undefined ? newMessagesNextToken : c.messagesNextToken,
                }
              }
              return c
            }),
          }))
        },

        pinConversation: (id) => {
          const state = get()
          const currentPinnedCount = state.conversations.filter(c => c.isPinned).length
          
          if (currentPinnedCount >= MAX_PINNED_CONVERSATIONS) {
            return { success: false, error: 'You can only pin up to 3 conversations.' }
          }

          set((state) => ({
            conversations: state.conversations.map((conv) =>
              conv.id === id ? { ...conv, isPinned: true } : conv
            ),
          }))
          
          return { success: true }
        },

        unpinConversation: (id) => {
          set((state) => ({
            conversations: state.conversations.map((conv) =>
              conv.id === id ? { ...conv, isPinned: false } : conv
            ),
          }))
        },

        rollbackPinState: (id, prevState) => {
          set((state) => ({
            conversations: state.conversations.map((conv) =>
              conv.id === id ? { 
                ...conv, 
                isPinned: prevState.isPinned,
                ...(prevState.originalCreatedAt && { createdAt: prevState.originalCreatedAt })
              } : conv
            ),
          }))
        },

        updateConversationPinMetadata: (id, isPinned, pinTime) => {
          set((state) => ({
            conversations: state.conversations.map((conv) => {
              if (conv.id === id) {
                return {
                  ...conv,
                  isPinned,
                  ...(pinTime && { createdAt: pinTime })
                }
              }
              return conv
            }),
          }))
        },

        selectPinnedConversations: () => {
          return get().conversations
            .filter((conv) => conv.isPinned)
            .sort((a, b) => b.createdAt - a.createdAt)
        },

        selectRegularConversations: () => {
          return get().conversations.filter((conv) => !conv.isPinned)
        },

        getPinnedConversationsCount: () => {
          return get().conversations.filter((conv) => conv.isPinned).length
        },

        getLastBotMessage: (conversationId) => {
          if (!conversationId) return null
          const conversation = get().conversations.find((c) => c.id === conversationId)
          if (!conversation || conversation.messages.length === 0) return null
          for (let i = conversation.messages.length - 1; i >= 0; i--) {
            if (conversation.messages[i].from === MessageSender.BOT) {
              return conversation.messages[i]
            }
          }
          return null
        },

        setPendingFirstMessage: (msg) => set({ pendingFirstMessage: msg }),

        setAwaitingResponse: (conversationId, isAwaiting) => {
          set((state) => ({
            conversations: state.conversations.map((c) =>
              c.id === conversationId ? { ...c, isAwaitingResponse: isAwaiting } : c
            ),
          }))
        },

        isSidebarCollapsed: false,
        toggleSidebar: () => {
          set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed }))
        },

        searchTerm: null,
        setSearchTerm: (term) => set({ searchTerm: term }),
      }),
      {
        name: 'conversations-storage',
        storage: createJSONStorage(() => sessionStorage),
      }
    )
  )
)

export default useConversationsStore
